import { SyntheticEvent } from 'react';
import { CircularProgress } from '@mui/material';
import { TabContext } from '@mui/lab';
import {
  Drawer,
  Flex,
  FlexCenter,
  FlexColumn,
  Tab,
  Tabs,
} from '@elementary/common-ui/components';
import { INCIDENT_OVERVIEW_TAB } from '@elementary/common-ui/constants/urlParams';
import PanelError from 'shared/components/PanelError';
import { ErrorBoundary } from 'react-error-boundary';
import styled from 'styled-components';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import useIncidentOverviewSidePanel from 'incidents/hooks/useIncidentOverviewSidePanel';
import useIncidentDetails from '../../hooks/useIncidentDetails';
import {
  isFullyUnmergedIncident,
  isIncidentReadOnly,
} from '../../utils/incidentState';
import IncidentDrawerTitle from './IncidentDrawerTitle';
import MergedIncidentAlert from './MergedIncidentAlert';
import SplitIncidentAlert from './SplitIncidentAlert';
import IncidentHeader from './IncidentHeader';
import IncidentMetadata from './IncidentMetadata';
import IncidentTickets from './IncidentTickets';
import IncidentSummary from './IncidentSummary';
import IncidentTabsPanel from './IncidentTabsPanel';
import IncidentAttributes from './IncidentAttributes';
import { getMonitorExecutionSource } from './IncidentExecutionHistoryTab';

type TabValue = 'lineage' | 'monitors' | 'timeline' | 'execution';

const BASE_TABS: TabValue[] = ['lineage', 'monitors', 'timeline'];

const PAGE_NAME = 'IncidentOverviewSidePanelV2';

const TabsWrapper = styled(Flex)`
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  flex-shrink: 0;
`;

const ErrorLoadingWrapper = styled(FlexCenter)`
  height: 100%;
  padding: 0 24px;
`;

const Content = styled(FlexColumn)`
  height: 100%;
  overflow: hidden;
  padding: 24px;
  gap: 16px;
`;

const IncidentOverviewSidePanel = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get(INCIDENT_OVERVIEW_TAB) as TabValue | null;

  const handleTabChange = (_event: SyntheticEvent, value: TabValue) => {
    setSearchParams((prev) => {
      prev.set(INCIDENT_OVERVIEW_TAB, value);
      return prev;
    });
  };

  const { selectedIncidentId, isOpen, onClose } =
    useIncidentOverviewSidePanel();

  const {
    data: incident,
    isLoading,
    error,
  } = useIncidentDetails(selectedIncidentId);
  const isFullyUnmerged = isFullyUnmergedIncident(incident);
  const isReadOnly = isIncidentReadOnly(incident);

  const isSingleSignal = incident?.monitors.length === 1;
  const showExecutionTab =
    !!isSingleSignal &&
    getMonitorExecutionSource(incident?.monitors[0]) != null;
  const defaultTab: TabValue = showExecutionTab ? 'execution' : 'lineage';
  const validTabs: TabValue[] = showExecutionTab
    ? ['execution', ...BASE_TABS]
    : BASE_TABS;
  const currentTab =
    tabParam && validTabs.includes(tabParam) ? tabParam : defaultTab;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={<IncidentDrawerTitle />}
      width="medium"
      noPadding
    >
      <ErrorBoundary
        resetKeys={[selectedIncidentId]}
        fallbackRender={({ error }) => (
          <ErrorLoadingWrapper>
            <PanelError
              error={error}
              page={PAGE_NAME}
              errorName="ReactRenderingError"
            />
          </ErrorLoadingWrapper>
        )}
      >
        {!!error && (
          <ErrorLoadingWrapper>
            <PanelError
              error={error as Error}
              page={PAGE_NAME}
              errorName="ApiError"
            />
          </ErrorLoadingWrapper>
        )}

        {isLoading ? (
          <ErrorLoadingWrapper>
            <CircularProgress />
          </ErrorLoadingWrapper>
        ) : (
          incident &&
          !error &&
          !isLoading && (
            <Content>
              <FlexColumn sx={{ flexShrink: 0, gap: '12px' }}>
                {incident.mergedIntoId && (
                  <MergedIncidentAlert mergedIntoId={incident.mergedIntoId} />
                )}
                {isFullyUnmerged && <SplitIncidentAlert incident={incident} />}
                <IncidentHeader incident={incident} />
                <IncidentMetadata incident={incident} />
                <IncidentAttributes
                  attributes={incident.attributes}
                  groupingStrategy={incident.groupingStrategy}
                />
                <IncidentTickets incident={incident} />
                <IncidentSummary
                  incidentId={incident.id}
                  summaries={incident.summaries}
                  isReadOnly={isReadOnly}
                />
              </FlexColumn>

              <TabContext value={currentTab}>
                <FlexColumn sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                  <TabsWrapper>
                    <Tabs value={currentTab} onChange={handleTabChange}>
                      {showExecutionTab && (
                        <Tab
                          label="Execution History"
                          value="execution"
                          wrapped
                        />
                      )}
                      <Tab label="Lineage" value="lineage" wrapped />
                      <Tab label="Monitors" value="monitors" wrapped />
                      <Tab label="Timeline" value="timeline" wrapped />
                    </Tabs>
                  </TabsWrapper>

                  <IncidentTabsPanel incident={incident} />
                </FlexColumn>
              </TabContext>
            </Content>
          )
        )}
      </ErrorBoundary>
    </Drawer>
  );
};

export default IncidentOverviewSidePanel;
